import React, { useEffect, useRef, useState } from "react";
import "./reward-entry.scss";
import logo from "../../assets/images/logo.png";

import usePWAInstall from "../../hooks/usePWAInstall";
import useOnlineStatus from "../../hooks/useOnlineStatus";

import {
  addQueuedReward,
  cancelRewardEntry,
  getRewardEntries,
} from "../../services/offlineQueueService";

import { syncQueuedRewards } from "../../services/rewardSyncService";

const SYNC_DELAY_MS = 10000;

const phoneRules = [
  /^(?:\+?60|0)1\d{8,9}$/,
  /^(?:\+?65)?[689]\d{7}$/,
  /^(?:\+?86)?1[3-9]\d{9}$/,
  /^(?:\+?66|0)[689]\d{8}$/,
  /^(?:\+?673)?[2378]\d{6}$/,
  /^(?:\+?62|0)8\d{8,11}$/,
];

const isValidPhone = (phone) => phoneRules.some((rule) => rule.test(phone));

const isValidAmount = (amount) => {
  const value = Number(amount);
  return !Number.isNaN(value) && value > 0 && value <= 500;
};

const normalizePhone = (phone) => {
  const cleanPhone = phone.replace(/\D/g, "");

  if (cleanPhone.startsWith("0")) {
    if (cleanPhone.startsWith("01")) return `60${cleanPhone.slice(1)}`;
    if (cleanPhone.startsWith("08")) return `62${cleanPhone.slice(1)}`;
  }

  return cleanPhone;
};

const getCountdownSeconds = (syncAfter) => {
  if (!syncAfter) return 0;

  const remaining = new Date(syncAfter).getTime() - Date.now();
  return Math.max(0, Math.ceil(remaining / 1000));
};

const RewardEntryPage = () => {
  const { canInstall, install } = usePWAInstall();
  const isOnline = useOnlineStatus();

  const phoneRef = useRef(null);
  const amountRef = useRef(null);

  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const [records, setRecords] = useState([]);
  const [visibleCount, setVisibleCount] = useState(10);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    refreshRecords();
  }, []);

  useEffect(() => {
    if (isOnline) {
      handleSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  useEffect(() => {
    const interval = setInterval(async () => {
      await refreshRecords();

      if (navigator.onLine) {
        await handleSync();
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSyncing]);

  const refreshRecords = async () => {
    const data = await getRewardEntries();
    setRecords(data);
  };

  const handleSync = async () => {
    if (isSyncing) return;

    setIsSyncing(true);
    await syncQueuedRewards();
    await refreshRecords();
    setIsSyncing(false);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  const resetForm = () => {
    setPhone("");
    setAmount("");

    setTimeout(() => {
      phoneRef.current?.focus();
    }, 100);
  };

  const handleRemoveDraft = async (localId) => {
    await cancelRewardEntry(localId);
    await refreshRecords();

    setMessage("Entry removed before sync.");
    setMessageType("success");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const normalizedPhone = normalizePhone(phone);
    const normalizedAmount = Number(amount).toFixed(2);

    if (!phone || !amount) {
      setMessage("Please enter customer phone number and order amount.");
      setMessageType("error");
      return;
    }

    if (!isValidPhone(phone) && !isValidPhone(normalizedPhone)) {
      setMessage(
        "Invalid phone number. Only Malaysia, Singapore, China, Thailand, Brunei and Indonesia numbers are allowed."
      );
      setMessageType("error");
      return;
    }

    if (!isValidAmount(amount)) {
      setMessage("Invalid amount. Amount must be more than RM0 and not more than RM500.");
      setMessageType("error");
      return;
    }

    setIsSubmitting(true);

    try {
      await addQueuedReward({
        phone_number: normalizedPhone,
        order_amount: normalizedAmount,
        status: "draft_queued",
        sync_after: new Date(Date.now() + SYNC_DELAY_MS).toISOString(),
      });

      setMessage("Reward saved. You have 10 seconds to remove it before syncing.");
      setMessageType("queued");

      resetForm();
      await refreshRecords();
    } catch (error) {
      console.error("Failed to save reward locally:", error);
      setMessage("Failed to save reward locally.");
      setMessageType("error");
    }

    setIsSubmitting(false);
  };

  const getStatusLabel = (item) => {
    if (item.status === "draft_queued") {
      const seconds = getCountdownSeconds(item.sync_after);
      return seconds > 0 ? `Queued (${seconds}s)` : "Queued";
    }

    if (item.status === "queued") return "Queued";
    if (item.status === "syncing") return "Syncing";
    if (item.status === "awarded") return "Awarded";
    if (item.status === "pending_customer_registration") return "Pending";
    if (item.status === "failed") return "Failed";
    if (item.status === "cancelled") return "Cancelled";

    return item.status;
  };

  const visibleRecords = records.slice(0, visibleCount);
  const hasMore = visibleCount < records.length;

  return (
    <div className="reward-entry">
      {!isOnline && (
        <div className="reward-entry__offline">
          ⚠️ No internet connection. Rewards will be saved and synced later.
        </div>
      )}

      <div className="reward-entry__header">
        <img className="reward-entry__logo" src={logo} alt="Pinwanwan" />

        <div className="reward-entry__actions">
          {canInstall && (
            <button type="button" className="reward-entry__install" onClick={install}>
              Install
            </button>
          )}

          <button type="button" className="reward-entry__logout" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      <form className="reward-entry__card" onSubmit={handleSubmit}>
        <h1 className="reward-entry__title">Customer Reward Entry</h1>

        <div className="reward-entry__field">
          <label className="reward-entry__label">Customer Phone Number</label>
          <input
            ref={phoneRef}
            className="reward-entry__input"
            type="tel"
            inputMode="numeric"
            placeholder="Example: 60123456789"
            value={phone}
            autoFocus
            onChange={(e) => {
              setPhone(e.target.value.replace(/[^\d+]/g, ""));
              setMessage("");
              setMessageType("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                amountRef.current?.focus();
              }
            }}
          />
        </div>

        <div className="reward-entry__field">
          <label className="reward-entry__label">Order Amount</label>
          <input
            ref={amountRef}
            className="reward-entry__input"
            type="text"
            inputMode="decimal"
            placeholder="Example: 12.30"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value.replace(/[^0-9.]/g, ""));
              setMessage("");
              setMessageType("");
            }}
          />
        </div>

        <button type="submit" className="reward-entry__submit" disabled={isSubmitting}>
          {isSubmitting ? "Saving..." : "Submit Reward"}
        </button>

        {message && (
          <div className={`reward-entry__message reward-entry__message--${messageType}`}>
            {message}
          </div>
        )}
      </form>

      <section className="reward-entry__recent">
        <div className="reward-entry__recent-header">
          <div>
            <h2 className="reward-entry__recent-title">Recent Records</h2>
          </div>

          <button type="button" className="reward-entry__refresh" onClick={handleSync}>
            Refresh
          </button>
        </div>

        <div className="reward-entry__table-wrap">
          <table className="reward-entry__table">
            <thead>
              <tr>
                <th>Phone</th>
                <th>Amount</th>
                <th>Points</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {visibleRecords.map((item) => {
                const countdown = getCountdownSeconds(item.sync_after);

                return (
                  <tr key={item.local_id || item.server_id}>
                    <td>{item.phone_number}</td>
                    <td>RM {Number(item.order_amount).toFixed(2)}</td>
                    <td>{item.points ?? "-"}</td>
                    <td>
                      <span
                        className={`reward-entry__status reward-entry__status--${item.status}`}
                      >
                        {getStatusLabel(item)}
                      </span>
                    </td>
                    <td>
                      {item.status === "draft_queued" && countdown > 0 ? (
                        <button
                          type="button"
                          className="reward-entry__remove"
                          onClick={() => handleRemoveDraft(item.local_id)}
                        >
                          Remove
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                );
              })}

              {records.length === 0 && (
                <tr>
                  <td colSpan="5" className="reward-entry__empty">
                    No records yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <button
            type="button"
            className="reward-entry__load-more"
            onClick={() => setVisibleCount((prev) => prev + 10)}
          >
            Load More
          </button>
        )}
      </section>
    {isSyncing && (
      <div className="reward-entry__sync-toast">
        Syncing records...
      </div>
    )}
    </div>
  );
};

export default RewardEntryPage;